import React, { useState, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import { useAuth } from "../../contexts/AuthContext";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { useNavigate } from "react-router-dom";

const Signup = () => {
  const { user } = useAuth();
  const { response, error, loading, fetchData } = useAxios();
  const navigate = useNavigate();
  const toast = useRef(null);

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    firstname: "",
    lastname: "",
    mobile: "",
    isAdmin: false,
    roles: [],
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email || !form.password || !form.firstname || !form.lastname) {
      toast.current.show({ severity: "warn", detail: "Заполните обязательные поля", life: 3000 });
      return;
    }

    if (form.password !== form.confirmPassword) {
      toast.current.show({ severity: "error", detail: "Пароли не совпадают", life: 3000 });
      return;
    }

    if (form.password.length < 6) {
      toast.current.show({ severity: "error", detail: "Пароль должен быть минимум 6 символов", life: 3000 });
      return;
    }

    const { confirmPassword, ...payload } = form;

    await fetchData({
      method: "post",
      url: "/api/signup",
      data: payload,
    });
  };

  React.useEffect(() => {
    if (!response) return;
    toast.current.show({ severity: "success", detail: response.message, life: 3000 });
    setForm({
      email: "",
      password: "",
      confirmPassword: "",
      firstname: "",
      lastname: "",
      mobile: "",
      isAdmin: false,
      roles: [],
    });
  }, [response]);

  React.useEffect(() => {
    if (!error) return;
    toast.current.show({ severity: "error", detail: error, life: 4000 });
  }, [error]);

  if (!user?.isAdmin) {
    return (
      <div className="flex justify-content-center mt-5">
        <Card className="w-full md:w-6">
          <p className="text-center text-red-500 font-bold">Доступ только для администраторов</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-content-center py-5">
      <div className="w-full md:w-8 lg:w-6 px-3">
        <Toast ref={toast} position="top-right" />

        <div className="flex align-items-center justify-content-between mb-2">
          <h1 className="text-xl font-bold m-0">Регистрация пользователя</h1>
          <Button
            label="Назад"
            icon="pi pi-arrow-left"
            className="p-button-text"
            onClick={() => navigate("/")}
          />
        </div>

        <Card>
          <form onSubmit={handleSubmit}>
            <h3 className="mt-0 mb-2">
              <i className="pi pi-user mr-2" style={{ color: '#2196f3' }}></i>
              Личные данные
            </h3>
            <Divider />

            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="firstname" className="block font-medium mb-1 text-sm">Имя *</label>
                  <InputText
                    id="firstname"
                    value={form.firstname}
                    onChange={(e) => handleChange("firstname", e.target.value)}
                    className="w-full p-inputtext-sm"
                    placeholder="Введите имя"
                    required
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="lastname" className="block font-medium mb-1 text-sm">Фамилия *</label>
                  <InputText
                    id="lastname"
                    value={form.lastname}
                    onChange={(e) => handleChange("lastname", e.target.value)}
                    className="w-full p-inputtext-sm"
                    placeholder="Введите фамилию"
                    required
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="email" className="block font-medium mb-1 text-sm">Email *</label>
                  <InputText
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="w-full p-inputtext-sm"
                    placeholder="user@example.com"
                    required
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="mobile" className="block font-medium mb-1 text-sm">Телефон</label>
                  <InputText
                    id="mobile"
                    value={form.mobile}
                    onChange={(e) => handleChange("mobile", e.target.value)}
                    className="w-full p-inputtext-sm"
                    placeholder="+992 XXX XX XX XX"
                  />
                </div>
              </div>
            </div>

            <h3 className="mt-2 mb-2">
              <i className="pi pi-lock mr-2" style={{ color: '#f59e0b' }}></i>
              Безопасность
            </h3>
            <Divider />

            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="password" className="block font-medium mb-1 text-sm">Пароль *</label>
                  <Password
                    id="password"
                    value={form.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    className="w-full"
                    inputClassName="w-full p-inputtext-sm"
                    placeholder="Минимум 6 символов"
                    toggleMask
                    feedback={false}
                    required
                  />
                </div>
              </div>

              <div className="col-12 md:col-6">
                <div className="field">
                  <label htmlFor="confirmPassword" className="block font-medium mb-1 text-sm">Повторите пароль *</label>
                  <Password
                    id="confirmPassword"
                    value={form.confirmPassword}
                    onChange={(e) => handleChange("confirmPassword", e.target.value)}
                    className="w-full"
                    inputClassName="w-full p-inputtext-sm"
                    placeholder="Повторите пароль"
                    toggleMask
                    feedback={false}
                    required
                  />
                </div>
              </div>
            </div>

            <h3 className="mt-2 mb-2">
              <i className="pi pi-shield mr-2" style={{ color: '#10b981' }}></i>
              Роли и доступ
            </h3>
            <Divider />

            <div className="grid">
              <div className="col-12">
                <div className="field">
                  <label htmlFor="roles" className="block font-medium mb-1 text-sm">Роли (нажмите Enter для добавления)</label>
                  <Chips
                    id="roles"
                    value={form.roles}
                    onChange={(e) => handleChange("roles", e.value)}
                    className="w-full"
                    placeholder="Введите роль и нажмите Enter"
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="field-checkbox">
                  <Checkbox
                    inputId="isAdmin"
                    checked={form.isAdmin}
                    onChange={(e) => handleChange("isAdmin", e.checked)}
                  />
                  <label htmlFor="isAdmin" className="ml-2 font-medium">Администратор (полный доступ ко всем разделам)</label>
                </div>
              </div>
            </div>

            <Divider />

            <div className="flex justify-content-end gap-2 mt-3">
              <Button
                type="button"
                label="Отмена"
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary p-button-sm"
                onClick={() => navigate("/")}
                disabled={loading}
              />
              <Button
                type="submit"
                label={loading ? "Создание..." : "Создать пользователя"}
                icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                disabled={loading}
                className="px-5 p-button-sm"
              />
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Signup;
